// A room's glazings in the 3D view (docs/claude/glazings.md): each solid of
// glazing.ts meshed in the room's frame, in its finish's look — a wood frame in
// the room's walls' wood —, picked as its door or its pane (roomGlazing.ts
// `glazingPartOf`).

import * as THREE from 'three';
import type {
  ProductInstance,
  Vec2,
} from '../../shared/types';
import {
  grainUVs,
  solidMesh,
} from '../glazing';
import {
  glazingLocalSolids,
  glazingLook,
  glazingPartOf,
  glazingToLocal,
  roomGlazings,
} from '../roomGlazing';
import type { SecPart } from '../section';
import { withLook } from './sceneNodes';

/** Every glazing solid of a room as a mesh in the world (`W`: the room's local
 *  plan to the world's), with what a click on it takes. `wood`: what a wood
 *  frame is built in — the walls' wood (scene3d.ts `roomMats`). */
export function glazingMeshes(inst: ProductInstance, W: (p: Vec2) => Vec2, wood: THREE.Material): Array<{ mesh: THREE.Mesh; part: SecPart }> {
  const out: Array<{ mesh: THREE.Mesh; part: SecPart }> = [];
  const mats = new Map<string, THREE.Material>();
  for (const g of roomGlazings(inst)) {
    for (const s of glazingLocalSolids(g)) {
      let mat = s.stuff === 'wood' ? wood : undefined;
      if (!mat) {
        const look = glazingLook(g, s.stuff);
        const key = `${look.color}|${look.opacity}|${look.metal}`;
        mat = mats.get(key);
        if (!mat) mats.set(key, mat = material(look));
      }
      const m = solidMesh(s.solid);
      const pos = new Float32Array(m.positions.length);
      for (let k = 0; k < m.positions.length; k += 3) {
        const l = glazingToLocal(g, { x: m.positions[k], y: m.positions[k + 1], z: m.positions[k + 2] });
        const w = W(l);
        pos[k] = w.x; pos[k + 1] = l.z; pos[k + 2] = -w.y;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      geo.setIndex(m.indices);
      // Flat: a profile's faces meet at sharp corners.
      const flat = geo.toNonIndexed();
      geo.dispose();
      flat.computeVertexNormals();
      if (s.stuff === 'wood') flat.setAttribute('uv', new THREE.Float32BufferAttribute(grainUVs(m.positions, m.indices), 2));
      out.push({ mesh: new THREE.Mesh(flat, mat), part: glazingPartOf(g, s.solid) });
    }
  }
  return out;
}

function material(look: { color: string; opacity: number; metal: boolean }): THREE.Material {
  const color = new THREE.Color(look.color);
  if (look.opacity < 1) {
    return withLook(new THREE.MeshPhysicalMaterial({ color, transparent: true, opacity: look.opacity, roughness: 0.05, metalness: 0, clearcoat: 0.5, clearcoatRoughness: 0.05, side: THREE.DoubleSide }), 'glass', look.color);
  }
  return withLook(new THREE.MeshStandardMaterial({ color, roughness: look.metal ? 0.35 : 0.7, metalness: look.metal ? 0.75 : 0, side: THREE.DoubleSide }), 'solid', look.color);
}
